import { useState, type ReactNode } from 'react';
import { showAlert } from '@/components/AppDialog';
import { useNavigate } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import Navbar from '@/component/navbar';
import Dh from '@/components/Dh';
import { API_BASE_URL } from '@/utils/env';
import { useSetupPricing } from '@/data/setupPricing';

const fmt = (n: number) => (<><Dh em="0.85" />{n.toLocaleString()}</>);

type PlanInfo = { name: string; price: number; billing: string };

export default function SetupInvoice() {
  const [confirmed, setConfirmed] = useState(false);
  const navigate = useNavigate();
  const P = useSetupPricing();

  const companyName = localStorage.getItem('setupCompanyName') || 'Company Name';
  const businessType = localStorage.getItem('setupBusinessType');
  const entityType = businessType === 'freezone' ? 'FZ-LLC' : 'LLC';
  const emirate = localStorage.getItem('setupEmirate') || 'Dubai';
  const setupPlan = localStorage.getItem('setupPlan') || 'starter';
  const setupExpedited = localStorage.getItem('setupExpedited');
  const setupUpgrade = localStorage.getItem('setupUpgrade');

  // Resolved from setupPricing/current rather than a hardcoded chain, so plans added
  // in /admin/setup-pricing bill correctly and a removed plan can't crash the invoice.
  const getPlanInfo = (): PlanInfo => {
    const titleOf = (id: string, fallback: string) => P.setupPlans.find(p => p.id === id)?.title || fallback;
    // Taking the Total Compliance upsell during the Starter flow replaces the plan line
    // with the upgrade price. Plans that are already Compliance/BiaB bill at their own price.
    if (setupPlan !== 'compliance' && setupPlan !== 'business-in-a-box' && setupUpgrade === 'compliance') {
      return { name: titleOf('compliance', 'Total Compliance'), price: P.upgradePrice, billing: 'Annual' };
    }
    return {
      name: titleOf(setupPlan, 'Starter'),
      price: P.plans[setupPlan]?.priceYr ?? 0,
      billing: 'Annual',
    };
  };

  const planInfo = getPlanInfo();
  const hasExpedited = setupPlan === 'starter' && setupExpedited === 'expedited';
  const expeditedFee = hasExpedited ? P.expeditedFee : 0;
  // Phase 1: government fees are Per Client Request (depend on business activity) — shown as
  // "PCR" and NOT added to the total; we charge our package fee only.
  const govFeePCR = P.govFeePCR;
  const govFee = govFeePCR ? 0 : P.govFee;
  const total = planInfo.price + expeditedFee + govFee;
  const govFeeDisplay = govFeePCR ? 'PCR (Per Client Request)' : null;

  const handleNext = async () => {
    if (!confirmed) return;
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      showAlert('Session expired. Please sign in again.');
      window.location.href = '/signup';
      return;
    }
    const user = JSON.parse(userStr);
    if (!user?.uid) {
      showAlert('Session error: user ID missing. Please sign in again.');
      window.location.href = '/signup';
      return;
    }
    try {
      const idToken = (() => { try { return JSON.parse(localStorage.getItem('authTokens') || '').idToken; } catch { return null; } })();
      const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify({
          // Include profile fields to ensure setup users are fully promoted from pendingUsers
          ...(user.firstName && { firstName: user.firstName }),
          ...(user.lastName && { lastName: user.lastName }),
          ...(user.email && { email: user.email }),
          signupUserType: 'setup',
          // Single source of truth for "accounting is activated" (mirrors consultingActivated).
          accountingActivated: true,
          businessSetupData: {
            type: 'incorporate',
            companyName,
            businessType: businessType || 'freezone',
            entityType,
            emirate,
            planName: planInfo.name,
            planKey: setupPlan,
            hasExpedited,
            hasUpgrade: setupUpgrade === 'compliance',
            total,
            submittedAt: new Date().toISOString(),
          },
        }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        console.error('Setup save failed:', resp.status, errData);
        showAlert(`Could not save your setup (${resp.status}). Please try again.`);
        return;
      }
    } catch (e) {
      console.error('Error saving setup data:', e);
      showAlert('Network error while saving your setup. Please try again.');
      return;
    }
    window.location.href = '/dashboard';
  };

  const Row = ({ label, value }: { label: string; value: ReactNode }) => (
    <div className="flex justify-between items-center py-1.5">
      <span className="text-sm font-semibold text-gray-800">{label}</span>
      <span className="text-sm text-gray-700">{value}</span>
    </div>
  );

  const SectionCard = ({
    title,
    editPath,
    children,
  }: {
    title: string;
    editPath: string;
    children: React.ReactNode;
  }) => (
    <div className="bg-white rounded-xl p-6 mb-4">
      <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
      <div className="divide-y divide-gray-100">{children}</div>
      <div className="flex justify-center mt-5">
        <button
          type="button"
          onClick={() => navigate(editPath)}
          className="px-7 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Edit
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Navbar hideSignBtn={false} />
      <div className="flex-1 px-8 py-10 max-w-5xl mx-auto w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">
          Your dream business is ready. Are you?
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Left column */}
          <div className="md:col-span-3">
            <SectionCard title="Company" editPath="/setup-company-name">
              <Row label="Preferred name" value={companyName} />
              <Row label="Entity type" value={entityType} />
              <Row label="Emirate" value={emirate} />
            </SectionCard>

            <SectionCard title="Subscription" editPath="/setup-plan">
              <Row label="Package" value={planInfo.name} />
              <Row label="Price" value={fmt(planInfo.price)} />
              <Row label="Billing" value={planInfo.billing} />
            </SectionCard>

            <SectionCard title="Government fee" editPath="/setup-emirate">
              <Row label="State" value={emirate} />
              <Row label="Price" value={govFeeDisplay ?? fmt(govFee)} />
              <Row label="Billing" value="One-time" />
            </SectionCard>

            <div className="bg-white rounded-xl p-5 mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-amber-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700 leading-relaxed">
                  By checking the box, i confirm the accuracy of the provided information
                </span>
              </label>
            </div>
          </div>

          {/* Right column */}
          <div className="md:col-span-2 space-y-4">
            <div className="rounded-xl p-6" style={{ backgroundColor: '#FFFBEB' }}>
              <h3 className="text-lg font-bold text-gray-900 mb-5">Your plan</h3>
              <div className="space-y-3 mb-5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">{planInfo.name}</span>
                  <span className="font-medium text-gray-900">{fmt(planInfo.price)}</span>
                </div>
                {hasExpedited && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Expedited Formation</span>
                    <span className="font-medium text-gray-900">{fmt(expeditedFee)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Government Fee</span>
                  <span className="font-medium text-gray-900">{govFeeDisplay ?? fmt(govFee)}</span>
                </div>
              </div>
              <div className="border-t border-amber-200 pt-4">
                <div className="flex justify-between items-baseline">
                  <span className="text-sm font-semibold text-gray-900">Total due today</span>
                  <span className="text-2xl font-bold text-gray-900">{fmt(total)}.00</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl p-5" style={{ backgroundColor: '#EFF6FF' }}>
              <Lightbulb className="w-6 h-6 text-blue-500 mb-3" strokeWidth={1.5} />
              <p className="text-sm text-gray-700 leading-relaxed">
                After Confirming, we'll gather additional details about your company, such as company members, registerd address, ownership breakdown, and more.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-8 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!confirmed}
            className="px-8 py-3 bg-amber-400 text-gray-900 font-semibold rounded-lg hover:bg-amber-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
